import type { IFeedDataClientDTO } from '~/shared/api/client/feed/feed.dto';
import {
  FEED_BANNER_TOP_POSITION,
  FEED_BANNER_BOTTOM_POSITION,
  FEED_BANNER_TOP_PARAMS,
  FEED_BANNER_BOTTOM_PARAMS,
} from '~/shared/lib/banners';

export type TBannerConfig = {
  ownerId: string;
  containerId: string;
  params: {
    pp: string;
    ps: string;
    p2: string;
    insertAfter?: string;
    insertPosition?: string;
    stick?: boolean;
    stickTo?: string;
    stickyColorScheme?: string;
  };
};

type TFeedBannerType = 'top' | 'bottom';

type TFeedRenderItem =
  | {
      type: 'news';
      data: IFeedDataClientDTO;
      key: string;
    }
  | {
      type: 'banner';
      bannerType: TFeedBannerType;
      bannerIndex: number;
      key: string;
    };

type TFeedBannerPosition = {
  position: number;
  bannerType: TFeedBannerType;
  bannerIndex: number;
};

export const FEED_BANNER_CONFIG_MAP: Record<TFeedBannerType, TBannerConfig[]> =
  {
    top: [
      FEED_BANNER_TOP_PARAMS,
      FEED_BANNER_TOP_PARAMS,
      FEED_BANNER_TOP_PARAMS,
    ],
    bottom: [
      FEED_BANNER_BOTTOM_PARAMS,
      FEED_BANNER_BOTTOM_PARAMS,
      FEED_BANNER_BOTTOM_PARAMS,
    ],
  };

export const getRepeatedFeedBannerPositions = (
  dataLength: number,
): TFeedBannerPosition[] => {
  const positions: TFeedBannerPosition[] = [];
  let topBannerIndex = 0;
  let bottomBannerIndex = 0;

  const addBannerPosition = (position: number, bannerType: TFeedBannerType) => {
    if (position > dataLength) {
      return;
    }

    const bannerIndex =
      bannerType === 'top' ? topBannerIndex++ : bottomBannerIndex++;

    positions.push({
      position,
      bannerType,
      bannerIndex,
    });
  };

  addBannerPosition(FEED_BANNER_TOP_POSITION, 'top');
  addBannerPosition(FEED_BANNER_BOTTOM_POSITION, 'bottom');

  let nextTopPosition = FEED_BANNER_BOTTOM_POSITION + 10;
  let nextBottomPosition = nextTopPosition + 5;

  while (nextTopPosition <= dataLength || nextBottomPosition <= dataLength) {
    addBannerPosition(nextTopPosition, 'top');
    addBannerPosition(nextBottomPosition, 'bottom');

    nextTopPosition += 15;
    nextBottomPosition += 15;
  }

  return positions;
};

export const buildFeedRenderItems = (
  data?: IFeedDataClientDTO[],
): TFeedRenderItem[] => {
  const items: TFeedRenderItem[] = [];
  const normalizedData = data ?? [];
  const bannerPositions = getRepeatedFeedBannerPositions(normalizedData.length);
  let bannerPositionIndex = 0;

  normalizedData.forEach((item, index) => {
    items.push({
      type: 'news',
      data: item,
      key: `feedNewsItem_${index}`,
    });

    const newsCount = index + 1;
    const currentBanner = bannerPositions[bannerPositionIndex];

    if (currentBanner && currentBanner.position === newsCount) {
      items.push({
        type: 'banner',
        bannerType: currentBanner.bannerType,
        bannerIndex: currentBanner.bannerIndex,
        key: `feedBanner_${currentBanner.bannerType}_${currentBanner.bannerIndex}`,
      });
      bannerPositionIndex += 1;
    }
  });

  return items;
};
